import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import ReactDOM from 'react-dom';
import { IconButton, makeStyles } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateIcon from '@material-ui/icons/Create';
import CheckIcon from '@material-ui/icons/Check';
import { simplify } from '@turf/turf';
import { Feature, Polygon } from 'geojson';
import { observer } from 'mobx-react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { CompToolFiltersStore } from '../../stores/compToolFiltersStore';

const SIMPLIFY_POLYGON_TOLERANCE = 0.0005;

interface FreehandDrawControlProps {
  map: mapboxgl.Map;
  lineColor?: string;
  lineWidth?: number;
  fillColor?: string;
  onStartDrawing?: () => void;
  onApply?: () => void;
  onComplete?: (coordinates: any[][]) => void;
  onClear?: () => void;
}

export const FreehandDrawControl = observer(({
  map,
  lineColor = '#FF0000',
  lineWidth = 2,
  fillColor = '#fbb03b',
  onStartDrawing = () => {/**Nothing to do here */ },
  onApply = () => {/**Nothing to do here */ },
  onComplete = () => {/**Nothing to do here */ },
  onClear = () => {/**Nothing to do here */ },
}: FreehandDrawControlProps) => {

  const allowDrawingRef = useRef(false);
  const controlRef = useRef<DrawControlUIHandle>(null);
  const { compToolStoreV2: compToolStore } = useStore();
  const compToolFiltersStore = compToolStore.compToolFiltersStore;

  useEffect(() => {
    let isDrawing = false;
    let coordinates: number[][] = [];
    const initializeDrawing = () => {
      const lineSource = map.getSource('freehand-line') as mapboxgl.GeoJSONSource
      if (!lineSource) {
        map.addSource('freehand-line', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
            properties: []
          },
        });
        map.addLayer({
          id: 'freehand-line-layer',
          type: 'line',
          source: 'freehand-line',
          paint: {
            'line-color': lineColor,
            'line-width': lineWidth,
          },
        });
      } else {
        lineSource.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
          properties: [],
        });
      }
    }
    const startDrawing = () => {
      if (!allowDrawingRef?.current) return;
      isDrawing = true;
      coordinates = [];
      onStartDrawing();
      // Disable map dragging
    };
    const draw = (e: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      if (!isDrawing) return;
      const { lng, lat } = e.lngLat;
      coordinates.push([lng, lat]);
      const source = map.getSource('freehand-line') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates,
          },
          properties: []
        });
      }
    };
    const endDrawing = () => {
      if (isDrawing && coordinates.length > 1) {
        coordinates.push(coordinates[0]);

        const geojson = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {}
        } as Feature<Polygon>;

        const simplified = simplify(geojson, { tolerance: SIMPLIFY_POLYGON_TOLERANCE, highQuality: true });

        onComplete(simplified.geometry.coordinates[0]);
        initializeDrawing();
      }
      isDrawing = false;
    };
    initializeDrawing();
    map.on('mousedown', startDrawing);
    map.on('mousemove', draw);
    map.on('mouseup', endDrawing);
    const onDrawClick = () => {
      if (allowDrawingRef.current) {
        map.dragPan.enable();
        allowDrawingRef.current = false;
        map.getCanvas().style.cursor = 'grab';
      } else {
        map.dragPan.disable();
        allowDrawingRef.current = true;
        map.getCanvas().style.cursor = 'crosshair';
      }

    }
    const onTrashClick = () => {
      console.log("Trash clicked");
      onClear();
    }
    const onApplyClick = () => {
      onApply();
      map.dragPan.enable();
      allowDrawingRef.current = false;
      controlRef.current.disableDraw();
      map.getCanvas().style.cursor = 'grab';
    }


    const customControl = new DrawControl(controlRef, onDrawClick, onTrashClick, onApplyClick, compToolFiltersStore);
    map.addControl(customControl, 'top-left');
    return () => {
      map.off('mousedown', startDrawing);
      map.off('mousemove', draw);
      map.off('mouseup', endDrawing);
      map.removeControl(customControl);
      if (map.getLayer('freehand-line-layer')) map.removeLayer('freehand-line-layer');
      if (map.getSource('freehand-line')) map.removeSource('freehand-line');
      if (map.getLayer('freehand-fill-layer')) map.removeLayer('freehand-fill-layer');
      if (map.getSource('freehand-fill')) map.removeSource('freehand-fill');
    };
  }, [map, lineColor, lineWidth, fillColor, onComplete, onClear, onApply, onStartDrawing]);
  return null;
});


class DrawControl implements mapboxgl.IControl {
  _container: HTMLElement;
  constructor(
    public ref: Ref<DrawControlUIHandle>,
    private onDrawClick: () => void,
    private onTrashClick: () => void,
    private onApplyClick: () => void,
    private compToolFiltersStore: CompToolFiltersStore
  ) {
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
  }
  onAdd(): HTMLElement {
    ReactDOM.render(
      <DrawControlUI ref={this.ref} onApplyClick={this.onApplyClick} onDrawClick={this.onDrawClick} onTrashClick={this.onTrashClick} compToolFiltersStore={this.compToolFiltersStore} />,
      this._container
    );
    return this._container;
  }
  onRemove() {
    ReactDOM.unmountComponentAtNode(this._container);
    this._container.remove();
  }
}

const useControlsStyles = makeStyles((theme) => ({
  button: {
    padding: 8,
    margin: 2,
    width: "26px !important",
    height: "26px !important",
    '&.Mui-disabled': {
      backgroundColor: '#f0f0f0',
      color: '#aaa',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  icon: {
    width: "16px",
    height: "16px",
    color: "#000000"
  }
}));

interface DrawControlUIProps {
  onApplyClick: () => void;
  onDrawClick: () => void;
  onTrashClick: () => void;
  compToolFiltersStore: CompToolFiltersStore;
}

interface DrawControlUIHandle {
  disableDraw: () => void;
}

const DrawControlUI = observer(forwardRef<DrawControlUIHandle, DrawControlUIProps>(
  ({ onApplyClick, onDrawClick, onTrashClick, compToolFiltersStore }, ref) => {
    const [active, setActive] = useState(false);
    const handleDrawClick = () => {
      setActive((prev) => !prev);
      onDrawClick();
    };

    useImperativeHandle(ref, () => ({
      disableDraw: () => setActive(false),
    }));

    const classes = useControlsStyles();

    return (
      <>
        <IconButton
          title="Apply"
          onClick={onApplyClick}
          className={classes.button}
          disabled={!compToolFiltersStore.drawingPolygons || compToolFiltersStore.drawingPolygons.length === 0}
        >
          <CheckIcon className={classes.icon} />
        </IconButton>
        <IconButton
          title="Draw"
          onClick={handleDrawClick}
          className={classes.button}
          style={{ backgroundColor: active ? '#d8d8d8' : 'white' }}
        >
          <CreateIcon className={classes.icon} />
        </IconButton>
        <IconButton
          title="Clear"
          onClick={onTrashClick}
          className={classes.button}
          disabled={!compToolFiltersStore.drawingPolygons || compToolFiltersStore.drawingPolygons.length === 0}
        >
          <DeleteIcon className={classes.icon} />
        </IconButton>
      </>
    );
  }
));